<script setup>

import {ref, reactive, computed, watch, onMounted, onUpdated, onBeforeUnmount, nextTick} from 'vue'
import SlideoutWithSlots from "../elements/slideoutWithSlots.vue";
import LeadQuickview from "./leadQuickview.vue";
import LeadsFilters from "./leadsFilters.vue";
import LeadsSorting from "./leadsSorting.vue";

const props = defineProps({
    canArchive: {
        type: Boolean,
        default: false,
    },
})

const leads = ref([]);
const totalLeads = ref(null);
const paginationLinks = ref([]);
const searchPossible = ref(true)
const fetchingLeadsStatus = ref('')
const loadingLeads = ref(false)
const selectedLeads = ref([])
const quickviewingLead = ref(null)
const quickviewSlideout = ref(null)
const salespersons = ref([])

const searchParameters = reactive({
    fuzzy: null,
    myOwnContacts: false,
    tags: [],
    excludeTags: [],
    emailStatus: 'regardless-email',
    phoneStatus: 'regardless-phone',
    unsubscribedStatus: 'regardless-unsubscribed',
    salesperson: 'all',
    orderBy: 'updated-lastest',
    perPage: 25,
    automationId: null,
    automationFilter: null,
});

const automationContext = reactive({
    id: null,
    filter: null,
    name: null,
});

const automationFilterStatusKeyMap = {
    opened: 'crm:automationFilter.status.opened',
    'not-opened': 'crm:automationFilter.status.notOpened',
    'not-delivered': 'crm:automationFilter.status.notDelivered',
};

const automationFilterActive = computed(() => Boolean(automationContext.id && automationContext.filter));
const automationFilterStatusLabel = computed(() => {
    const statusKey = automationFilterStatusKeyMap[automationContext.filter] ?? null;
    return statusKey ? _mft(statusKey) : '';
});
const automationFilterMessage = computed(() => {
    if (! automationFilterActive.value) {
        return '';
    }

    if (automationContext.name) {
        return _mft('crm:automationFilter.messageWithName', { name: automationContext.name });
    }

    return _mft('crm:automationFilter.messageWithoutName');
});

const makeFetchLeadsRequest = async (link) => {
    let url = route('api.crm.filterLeads') + '?' + searchParametersUrlParams.value

    if (link !== null) {
        url = link.url + '&' + searchParametersUrlParams.value
    }
    try {
        fetchingLeadsStatus.value = 'waiting'
        loadingLeads.value = true
        const response = await axios.get(url);
        leads.value = response.data.leads.data;
        totalLeads.value = response.data.total_count;
        paginationLinks.value = response.data.leads.links;

        fetchingLeadsStatus.value = ''
        loadingLeads.value = false
        searchPossible.value = false;

    } catch (error) {
        fetchingLeadsStatus.value = 'error'
    }
}

const fetchAvailableParameters = async () => {
    const res = await axios.get(route('api.crm.availableParameters'));

    salespersons.value = [
        { value: 'all', label: _mft('crm:filters.owner.all') },
        ...res.data.salespersons.map(salesperson => ({
            value: salesperson.id,
            label: salesperson.name,
        })),
    ]
}

const fetchLeads = (link = null) => {
    if (! searchPossible.value) {
        return
    }

    makeFetchLeadsRequest(link)
};

const onMultiDeleted = () => {
    selectedLeads.value = []
    makeFetchLeadsRequest(null);
}

const onMultiReload = () => {
    selectedLeads.value = []
    makeFetchLeadsRequest(null);
}

const onFiltersApplied = (filters) => {
    Object.assign(searchParameters, filters)
    selectedLeads.value = []
    searchPossible.value = true
    saveFilters()
    updateSearch()
}

const onSortingApplied = (sorting) => {
    Object.assign(searchParameters, sorting)
    selectedLeads.value = []
    searchPossible.value = true
    saveFilters()
    updateSearch()
}

const updateSearch = () => {
    if (! searchPossible.value) {
        return
    }
    leads.value = [];
    totalLeads.value = null;
    paginationLinks.value = [];

    nextTick(() => {
        fetchLeads();
    })
};

const removeAutomationParamsFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('automationId');
    url.searchParams.delete('automationFilter');
    url.searchParams.delete('automationName');

    const newSearch = url.searchParams.toString();
    const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;

    window.history.replaceState({}, '', newUrl);
};

const applyAutomationFilterFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filter = params.get('automationFilter');
    const id = params.get('automationId');
    const name = params.get('automationName');

    if (filter && id) {
        automationContext.id = id;
        automationContext.filter = filter;
        automationContext.name = name;
        searchParameters.automationId = id;
        searchParameters.automationFilter = filter;
        searchParameters.myOwnContacts = false;
    } else {
        automationContext.id = null;
        automationContext.filter = null;
        automationContext.name = null;
        searchParameters.automationId = null;
        searchParameters.automationFilter = null;
    }
};

const clearAutomationFilter = () => {
    automationContext.id = null;
    automationContext.filter = null;
    automationContext.name = null;
    searchParameters.automationId = null;
    searchParameters.automationFilter = null;
    removeAutomationParamsFromUrl();
    selectedLeads.value = [];
    searchPossible.value = true;
    updateSearch();
};

const saveFilters = () => {
    const persistentParameters = {
        myOwnContacts: searchParameters.myOwnContacts,
        tags: searchParameters.tags,
        excludeTags: searchParameters.excludeTags,
        emailStatus: searchParameters.emailStatus,
        phoneStatus: searchParameters.phoneStatus,
        unsubscribedStatus: searchParameters.unsubscribedStatus,
        salesperson: searchParameters.salesperson,
        orderBy: searchParameters.orderBy,
        perPage: searchParameters.perPage,
    }

    _mfPrefSet('crm-search-parameters', JSON.stringify(persistentParameters))
}

const setPage = (link) => {
    searchPossible.value = true
    fetchLeads(link)
}

const selectLead = (lead) => {
    if (selectedLeads.value.includes(lead)) {
        selectedLeads.value = selectedLeads.value.filter((id) => id !== lead);
    } else {
        selectedLeads.value.push(lead);
    }
}

const selectAllLeads = () => {
    if (selectedLeads.value.length === leads.value.length) {
        selectedLeads.value = [];
    } else {
        selectedLeads.value = leads.value;
    }
}

const quickviewLead = (lead) => {
    quickviewingLead.value = lead;
}

const updateLeadFromQuickview = (quickViewLead) => {
    // This is a quick hack to avoid creating a new component and everything
    // We really only care about updating visible fields in the table anyway
    quickviewingLead.value.name = quickViewLead.name
    quickviewingLead.value.email = quickViewLead.email
}

// Archived or deleted from the quickview: the lead leaves the list.
const onLeadRemovedFromQuickview = () => {
    quickviewSlideout.value.close({force: true})
    selectedLeads.value = []
    makeFetchLeadsRequest(null);
}

const searchParametersUrlParams = computed(() => {
    return Object.keys(searchParameters).map((key) => {
        return key + '=' + searchParameters[key]
    }).join('&')
})

const selectedLeadIds = computed(() => {
    return selectedLeads.value.map(lead => lead.id)
})

watch(searchParameters, (oldVal, newVal) => {
    searchPossible.value = true;
});

onMounted(() => {

    fetchAvailableParameters()

    const params = new URLSearchParams(window.location.search);
    if (params.get('tag-ids')) {
        searchParameters.tags = params.get('tag-ids').split(',').map(id => parseInt(id, 10));
        fetchLeads();
        return;
    }

    _mfPrefGet('crm-search-parameters').then(data => {
        if (data) {
            searchParameters.myOwnContacts = false
            searchParameters.tags = data.tags ?? []
            searchParameters.excludeTags = data.excludeTags ?? []
            searchParameters.emailStatus = data.emailStatus ?? 'regardless-email'
            searchParameters.phoneStatus = data.phoneStatus ?? 'regardless-phone'
            searchParameters.unsubscribedStatus = data.unsubscribedStatus ?? 'regardless-unsubscribed'
            searchParameters.salesperson = data.salesperson ?? 'all'
            searchParameters.orderBy = data.orderBy ?? 'updated-lastest'
            searchParameters.perPage = data.perPage ?? 25
        }

        searchParameters.automationId = null
        searchParameters.automationFilter = null

        applyAutomationFilterFromUrl();
        fetchLeads();
    })
});
</script>

<template>
    <div>
        <div>
            <div class="flow-root mt-8">
                <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div class="relative flex items-center space-x-2 space-between">
                        <div class="w-full relative">
                            <input-text
                                type="text"
                                :placeholder="_mft('crm:filters.search.placeholder')"
                                v-model:content="searchParameters.fuzzy"
                                @keydown.enter="updateSearch"
                                class="w-full !mb-1"
                            />
                            <div class="flex items-center absolute top-0 right-0 bottom-0 pr-1">
                                <button-base
                                    :disabled="! searchPossible"
                                    :style-type="searchPossible ? 'primary' : 'disabled'"
                                    @click="updateSearch"
                                    :state="fetchingLeadsStatus"
                                    size="input-embedded"
                                >
                                    {{ _mft('crm:filters.search') }}
                                </button-base>
                            </div>
                        </div>

                        <leads-filters
                            :search-parameters="searchParameters"
                            :salespersons="salespersons"
                            @apply="onFiltersApplied"
                        />
                        <leads-sorting
                            :search-parameters="searchParameters"
                            @apply="onSortingApplied"
                        />
                    </div>

                    <card-sub
                        v-if="automationFilterActive"
                        class="mt-1"
                    >
                        <div class="flex flex-col gap-3  md:flex-row md:items-center md:justify-between">
                            <div class="space-y-1">
                                <span class="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {{ _mft('crm:automationFilter.bannerTitle') }}
                                </span>
                                <span class="block text-sm font-medium">{{ automationFilterMessage }}</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span
                                    v-if="automationFilterStatusLabel"
                                    class="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-gray-700 bg-white rounded-full border border-gray-200"
                                >
                                    {{ automationFilterStatusLabel }}
                                </span>
                                <button-base size="sm" style-type="secondary" @click="clearAutomationFilter">
                                    {{ _mft('crm:automationFilter.clear') }}
                                </button-base>
                            </div>
                        </div>
                    </card-sub>

                    <div class="flex justify-center items-center mb-2 mt-2">
                        <div
                            class="space-x-2 font-thin text-center text-gray-500 mr-4"
                        >
                            <div v-if="loadingLeads" class="flex justify-center">
                                &nbsp;
                                <mf-spinner-medium class="w-8 h-4" />
                                &nbsp;
                            </div>
                            <div
                                v-if="! loadingLeads"
                                class="text-xs lowercase transition-opacity"
                                :class="totalLeads !== null ? 'opacity-100' : 'opacity-0'"
                            >
                                <span>{{ totalLeads }} {{ _mft('crm:contacts') }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white rounded-xl border">
                        <table class="w-full text-left">
                            <thead class="sticky inset-0">
                                <tr class="border-b border-b-gray-200">
                                    <th class="text-center min-w-5 relative px-1 xl:px-0">
                                        <div class="absolute inset-0 flex items-center justify-center z-10">
                                            <i
                                                class="text-xl text-gray-500 transition-all duration-75 cursor-pointer fa-thin"
                                                :class="leads.length > 0 && selectedLeadIds.length === leads.length ? 'fa-square-check' : 'fa-square fa-thin opacity-50'"
                                                @click="selectAllLeads()"
                                            ></i>
                                        </div>
                                    </th>
                                    <th scope="col" class="relative isolate py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">
                                        {{ _mft('crm:leadIndex.columnLabel.name') }}
                                    </th>
                                    <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell">
                                        {{ _mft('crm:leadIndex.columnLabel.company') }}
                                    </th>
                                    <th scope="col" class="relative py-3.5 pl-3">
                                        <span class="sr-only"></span>
                                    </th>
                                    <th scope="col" class="z-20 relative py-3.5 pl-3">
                                        <span class="sr-only">
                                            {{ _mft('crm:leadIndex.columnLabel.tools') }}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody
                                v-if="leads != null"
                                class="divide-y"
                            >
                                <tr
                                    v-for="lead in leads"
                                    :key="lead.id"
                                    class="group"
                                >
                                    <td class="text-center relative px-2">
                                        <div
                                            class="flex items-center justify-center z-10 transition-opacity"
                                            :class="selectedLeadIds.includes(lead.id) ? 'text-black' : 'opacity-50 group-hover:opacity-100 text-black'"
                                        >
                                            <i
                                                class="text-xl transition-all duration-75 cursor-pointer fa-thin"
                                                :class="selectedLeadIds.includes(lead.id) ? 'fa-square-check text-gray-500' : 'fa-square text-gray-500'"
                                                @click="selectLead(lead)"
                                            ></i>
                                        </div>
                                    </td>
                                    <td class="py-4 pr-3 text-sm font-medium text-gray-900">
                                        <div class="flex items-center">
                                            <div class="justify-center items-center mr-2 lg:mr-3 hidden md:flex">
                                                <div class="w-8 h-8 bg-wlPrimary text-gray-100 flex justify-center items-center rounded-full text-xs font-light tracking-tighter">
                                                    {{ lead.name[0] }}
                                                    {{ lead.name.split(' ').length > 1 ? lead.name.split(' ')[1][0] : '' }}
                                                </div>
                                            </div>
                                            <div>
                                                <a
                                                    @click.prevent="quickviewLead(lead)"
                                                    :href="route('crm.leads.view', lead.id)"
                                                    class="text-black cursor-pointer"
                                                >
                                                    {{ lead.name }}
                                                </a><br>
                                                <small>{{ lead.email }}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="hidden px-3 py-4 text-sm text-gray-700 md:table-cell">
                                        {{ lead.title }}<br>
                                        <small>{{ lead.company }}</small>
                                    </td>
                                    <td class="relative py-4 pl-3 text-sm font-medium text-right">
                                        &nbsp;
                                    </td>
                                    <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right">
                                        <div class="flex flex-col items-end justify-end space-y-1 md:flex-row md:space-y-0 md:space-x-1 md:items-start">
                                            <button-secondary
                                                size="sm"
                                                @click="quickviewLead(lead)"
                                            >
                                                {{ _mft('crm:lead.quickview') }}
                                            </button-secondary>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <nav class="flex items-center justify-between px-4 mt-8 sm:px-0">
            <div class="flex flex-1 w-0 -mt-px">
            </div>
            <div class="flex md:-mt-px">
                <a
                    v-for="(link, i) in paginationLinks"
                    :key="i"
                    :href="link.url"
                    class="relative w-8 h-8 transition-all border rounded-wl first:border-transparent last:border-transparent"
                    :class="[link.active ? 'items-center text-sm font-medium text-gray-600': 'hidden first:inline-flex last:inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700', link.active ? paginationLinks.length < 12 ? 'border-gray-300' : 'border-transparent' : paginationLinks.length >= 12 ? 'hidden' : 'border-transparent md:inline-flex']"
                    aria-current="page"
                    @click.prevent="setPage(link)"
                >
                    <span class="absolute inset-0 flex items-center justify-center">
                        <span class="" v-html="link.label"></span>
                    </span>
                </a>
            </div>
            <div class="flex justify-end flex-1 w-0 -mt-px">
            </div>
        </nav>

        <leads-multi-manage
            :leads="selectedLeads"
            :can-archive="props.canArchive"
            @deleted="onMultiDeleted"
            @deselect="selectedLeads = []"
            @reload="onMultiReload"
        />

        <slideout-with-slots
            v-if="quickviewingLead"
            ref="quickviewSlideout"
            @close="quickviewingLead = null"
            max-width-class="almost-full-screen"
        >
            <template #title>
                <h2>
                    <a :href="route('crm.leads.view', quickviewingLead?.id)" class="mf-link">
                        {{ quickviewingLead.name }}
                    </a>
                </h2>
            </template>
            <template #actions>
                <div class="flex space-x-2">
                    <!-- Single.vue teleports the lead's actions and Closer buttons here when rendered as a widget.
                         Layered above the z-modal body so the actions dropdown isn't hidden behind it. -->
                    <div id="lead-quickview-top-bar" class="relative z-modal-toggler"></div>
                    <button-primary @click="quickviewSlideout.close()">{{ _mft('shared:done') }}</button-primary>
                </div>
            </template>
            <template #body>
                <div class="relative z-modal">
                    <lead-quickview
                        :lead="quickviewingLead"
                        @update:lead="updateLeadFromQuickview"
                        @archived="onLeadRemovedFromQuickview"
                        @deleted="onLeadRemovedFromQuickview"
                    />
                </div>
            </template>
        </slideout-with-slots>


    </div>

</template>

<style>

</style>
